import { z } from 'zod';

/**
 * Contract for `storage_locations` (Phase 3.2 of
 * `docs/superpowers/plans/2026-10-14-harness/roadmap.md`, `docs/TECHNICAL-AUDIT.md` F-001).
 *
 * Replaces `storage-locations-api.ts`'s hand-written `StorageLocation` interface, which carried
 * ZERO runtime check that a response body actually had this shape — exactly the gap that let
 * `ff309b7`'s wrong-envelope-unwrapping bug ship with a green suite (the test mocked a shape that
 * didn't match the live response). This schema is read directly from `nexvia-api-web`'s real
 * source, not from this repo's own (previously-correct, but re-verified here, not trusted blindly)
 * interface:
 *
 * - `nexvia-api-web/src/database/migrations/1788800000000-CreateStorageLocations.js` — the table's
 *   real columns: `id` (serial PK), `storage_profile_id` (FK, never serialized to the client —
 *   confirmed below), `department` varchar(100), `locality` varchar(255), `address` text, `photos`
 *   jsonb array default `'[]'`, `created_at`/`updated_at` timestamptz.
 * - `nexvia-api-web/src/modules/storage/entities/storage-location.entity.ts` — same columns,
 *   camelCased (`createdAt`/`updatedAt`); confirms `storageProfile` is a relation, not a plain
 *   column, and is never selected by the repository methods below (no `relations:` option passed).
 * - `nexvia-api-web/src/modules/storage/repositories/storage-location.repository.ts` —
 *   `findAllByStorageProfile`/`create`/`update` all return the plain TypeORM row (`repo.find`/
 *   `repo.save`/`repo.findOne` with no `relations` option), so the wire shape really is
 *   `{id, department, locality, address, photos, createdAt, updatedAt}` — **not** just the five
 *   fields (`id`/`department`/`locality`/`address`/`photos`) the old hand-written interface
 *   claimed; `createdAt`/`updatedAt` are real fields this repo's frontend has simply never needed
 *   yet. Modeled here as optional so the schema reflects the real wire shape without forcing every
 *   test fixture (and this module's own exported `StorageLocation` type, unused by any page today)
 *   to carry two timestamps nothing reads.
 * - `nexvia-api-web/src/modules/storage/storage.controller.ts` — `listLocations`/`createLocation`/
 *   `updateLocation` all answer `res.status(2xx).json({ success: true, data })`; `deleteLocation`
 *   answers `res.status(204).send()`, no body — nothing to parse for delete, confirmed by reading
 *   the controller directly (re-confirms `storage-locations-api.ts`'s own docblock, which already
 *   said this correctly).
 *
 * `storage-locations-api.ts`'s generic `apiGet`/`apiPost`/`apiPatch` transport (shared with
 * `locations-catalog.ts`'s unrelated BARE `/locations/departments` and `/locations/localities`
 * endpoints — see that module's own docblock) already shape-detects and strips the
 * `{success,data}` envelope before any caller sees the body. These schemas therefore validate the
 * ALREADY-UNWRAPPED payload (a location, or an array of them) — the shape this module's own
 * exported functions hand back to callers — not the raw `{success,data}` wrapper. That keeps the
 * generic envelope-unwrapping (reused by the unrelated bare-response catalog endpoints) untouched,
 * while still making the final shape this module promises a real, checked contract instead of an
 * unchecked type assertion.
 */
export const StorageLocationSchema = z.object({
  id: z.int(),
  department: z.string(),
  locality: z.string(),
  address: z.string(),
  photos: z.array(z.string()),
  // NEX-1168 (plan 2026-10-20 Tarea 2.1/2.7) — where the address is, from the panel's address
  // autocomplete (`storage-location.entity.ts`, `DECIMAL_AS_NUMBER`: numbers, not strings); `null`
  // for an address typed by hand. Optional, not defaulted: an API before NEX-1168 does not send the
  // keys, and a location without them reads exactly as one without coordinates.
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  placeId: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional()
});

export const StorageLocationListSchema = z.array(StorageLocationSchema);

export type StorageLocationContract = z.infer<typeof StorageLocationSchema>;
